import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { isIOS, webOrigin } from "@/lib/nativeBridge";
import IosPurchaseNotice from "@/components/IosPurchaseNotice";
import AppShell from "@/components/AppShell";
import CompBanner from "@/components/CompBanner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { formatDate, formatApiError } from "@/lib/utils-app";
import { toast } from "sonner";
import { ArrowsLeftRightIcon as ArrowsLeftRight, CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, ClockIcon as Clock, GavelIcon as Gavel, ShieldCheckIcon as ShieldCheck } from "@phosphor-icons/react";
import EmptyState from "@/components/EmptyState";
import { RowListSkeleton } from "@/components/SkeletonList";
import MicrochipVerifyDialog from "@/components/MicrochipVerifyDialog";

const STATUS_LABELS = {
  pending_buyer_accept: { label: "Awaiting buyer", tone: "bg-[#FBF7EC] text-[#8A6A00] border-[#C9A227]" },
  pending_seller_accept: { label: "Awaiting current owner", tone: "bg-[#FBF7EC] text-[#8A6A00] border-[#C9A227]" },
  pending_admin_approve: { label: "Awaiting admin review", tone: "bg-[#EEF5FF] text-[#1F4C8A] border-[#93B6E5]" },
  approved: { label: "Approved", tone: "bg-[#EEFBEC] text-[#2F6E2F] border-[#8ED08E]" },
  rejected: { label: "Rejected", tone: "bg-[#FDEEEE] text-[#A12125] border-[#EFA1A3]" },
};

export default function Transfers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);
  // Pending action staged behind microchip verification.
  const [chipDialog, setChipDialog] = useState(null); // { transferId, dogName, action: 'accept' | 'pay' }

  const load = () =>
    api
      .get("/transfers/mine")
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const proceedAction = async (transfer, kind) => {
    if (kind === "pay") return payAndAccept(transfer.id);
    return act(transfer.id, "accept");
  };

  const guardedAccept = (transfer, kind) => {
    // Only buyers acting on a pending_buyer_accept transfer for a dog
    // with a microchip on file need to verify before continuing.
    const needsChip =
      transfer.status === "pending_buyer_accept" &&
      transfer.to_user_id === user?.id &&
      transfer.microchip_required &&
      !transfer.microchip_verified;
    if (needsChip) {
      setChipDialog({
        transferId: transfer.id,
        dogName: transfer.dog?.name,
        action: kind,
      });
      return;
    }
    proceedAction(transfer, kind);
  };

  const onChipVerified = async () => {
    if (!chipDialog) return;
    const transferId = chipDialog.transferId;
    const action = chipDialog.action;
    setChipDialog(null);
    // Refresh row state so microchip_verified flag is current, then proceed.
    await load();
    const refreshed = (
      await api.get("/transfers/mine").catch(() => ({ data: [] }))
    ).data.find((t) => t.id === transferId);
    if (refreshed) {
      proceedAction(refreshed, action);
    } else {
      toast.error("Transfer not found after verification.");
    }
  };

  const act = async (id, kind, reason = "") => {
    setBusy(id);
    try {
      if (kind === "accept") {
        await api.post(`/transfers/${id}/accept`);
        toast.success("Transfer accepted — now awaiting admin review.");
      } else {
        await api.post(`/transfers/${id}/reject`, { reason });
        toast.success("Transfer withdrawn.");
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  // Buyer pays the $15 fee for a seller-initiated transfer; on payment
  // success Stripe redirects back and the transfer flips to
  // pending_admin_approve automatically.
  const payAndAccept = async (transferId) => {
    setBusy(transferId);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: "transfer_ownership",
        origin_url: webOrigin(),
        transfer_id: transferId,
      });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      // Promo code or unexpected free path — refresh and notify
      toast.success("Transfer accepted — now awaiting admin review.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(null);
    }
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight" data-testid="transfers-heading">
              Ownership transfers
            </h1>
            <p className="text-[#6B6B6B] mt-2">
              Track dogs being transferred to or from you.
            </p>
          </div>
        </div>

        <CompBanner />

        {loading ? (
          <RowListSkeleton count={3} testId="transfers-loading-skeleton" />
        ) : rows.length === 0 ? (
          <EmptyState
            testId="transfers-empty"
            icon={<ArrowsLeftRight size={32} weight="duotone" />}
            title="No transfers yet"
            description="Start a transfer from any dog's detail page — once initiated it'll move through buyer accept, payment, and admin approval here."
            ctaLabel="Go to my dogs"
            ctaTo="/dogs"
            secondaryLabel="Browse marketplace"
            secondaryTo="/marketplace"
          />
        ) : (
          <div className="space-y-3" data-testid="transfers-list">
            {rows.map((t) => {
              const isSeller = t.from_user_id === user?.id;
              const isBuyer = t.to_user_id === user?.id;
              const canAccept =
                (t.status === "pending_buyer_accept" && isBuyer) ||
                (t.status === "pending_seller_accept" && isSeller);
              const buyerMustPay =
                t.status === "pending_buyer_accept" &&
                isBuyer &&
                t.initiated_by === "seller" &&
                !t.paid;
              const canReject =
                t.status !== "approved" && t.status !== "rejected";
              const statusMeta = STATUS_LABELS[t.status] || { label: t.status, tone: "" };
              return (
                <div
                  key={t.id}
                  className="bg-white border border-[#E5E5E5] rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                  data-testid={`transfer-row-${t.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to={`/dogs/${t.dog_id}`}
                        className="font-spectral text-xl tracking-tight hover:text-crimson transition-colors"
                      >
                        {t.dog?.name || "Unknown dog"}
                      </Link>
                      {t.dog?.registration_number && (
                        <span className="text-xs font-mono text-[#6B6B6B]">
                          {t.dog.registration_number}
                        </span>
                      )}
                      <Badge variant="outline" className={`${statusMeta.tone} text-xs font-bold uppercase tracking-wider`}>
                        {statusMeta.label}
                      </Badge>
                    </div>
                    <p className="text-sm text-[#6B6B6B] mt-1">
                      {isSeller ? "To: " : "From: "}
                      <strong>
                        {isSeller
                          ? t.to_user?.name || t.to_name || t.to_email
                          : t.from_user?.name || ""}
                      </strong>
                      {"  ·  "}
                      <span className="text-[#8B8B8B]">
                        Initiated {formatDate(t.created_at)}
                      </span>
                    </p>
                    {t.message && (
                      <p className="text-sm text-[#555] mt-2 italic bg-[#FAFAFA] border-l-2 border-[#C9A227] pl-3 py-1">
                        "{t.message}"
                      </p>
                    )}
                    {t.rejection_reason && (
                      <p className="text-sm text-[#A12125] mt-2">
                        Reason: {t.rejection_reason}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {canAccept && buyerMustPay && isIOS() && (
                      <IosPurchaseNotice label="The transfer fee" showPlans={false} />
                    )}
                    {canAccept && buyerMustPay && !isIOS() && (
                      <Button
                        disabled={busy === t.id}
                        onClick={() => guardedAccept(t, "pay")}
                        className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
                        data-testid={`transfer-pay-accept-${t.id}`}
                      >
                        <CheckCircle size={14} className="mr-1" weight="duotone" />
                        Pay $15 & accept
                      </Button>
                    )}
                    {canAccept && !buyerMustPay && (
                      <Button
                        disabled={busy === t.id}
                        onClick={() => guardedAccept(t, "accept")}
                        className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
                        data-testid={`transfer-accept-${t.id}`}
                      >
                        <CheckCircle size={14} className="mr-1" weight="duotone" />
                        Accept
                      </Button>
                    )}
                    {t.status === "pending_buyer_accept" && isBuyer && t.microchip_required && (
                      <span
                        className={`text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${
                          t.microchip_verified
                            ? "bg-[#EEFBEC] border-[#8ED08E] text-[#2F6E2F]"
                            : "bg-[#FFF8E5] border-[#FFD37D] text-[#8A6A00]"
                        }`}
                        data-testid={`microchip-status-${t.id}`}
                      >
                        <ShieldCheck size={11} weight="duotone" />
                        {t.microchip_verified ? "Microchip verified" : "Microchip check required"}
                      </span>
                    )}
                    {canReject && (
                      <Button
                        disabled={busy === t.id}
                        onClick={() => {
                          const r = window.prompt("Reason (optional):", "");
                          if (r === null) return; // user hit cancel
                          act(t.id, "reject", r);
                        }}
                        variant="outline"
                        className="rounded-full border-[#E5E5E5] text-crimson"
                        data-testid={`transfer-reject-${t.id}`}
                      >
                        <XCircle size={14} className="mr-1" weight="duotone" />
                        {isSeller ? "Withdraw" : "Decline"}
                      </Button>
                    )}
                    {t.status === "pending_admin_approve" && (
                      <span className="text-xs text-[#6B6B6B] inline-flex items-center gap-1">
                        <Gavel size={12} weight="duotone" /> Waiting on admin
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <MicrochipVerifyDialog
        open={!!chipDialog}
        onOpenChange={(o) => !o && setChipDialog(null)}
        transferId={chipDialog?.transferId}
        dogName={chipDialog?.dogName}
        onVerified={onChipVerified}
      />
    </AppShell>
  );
}
